// This function is used to check if a country requires a postal code
// Check based on a postal code pattern per country code
export function countryRequiresPostalCode(countryCode: string): boolean {
  return countryCode in postalCodePatterns;
}

/**
 * Pattern tokens:
 * - N: digit
 * - A: letter
 * - X: alphanumeric
 * - space / dash / other: literal character
 * A country is valid if the postal code matches any of its patterns.
 */
export function isPostalCodeValidForCountry(postalCode: string, countryCode: string): boolean {
  const patterns = postalCodePatterns[countryCode];
  if (!patterns) return true;

  const normalized = postalCode.trim();
  return patterns.some((pattern) => matchesPostalCodePattern(normalized, pattern));
}

function matchesPostalCodePattern(postalCode: string, pattern: string): boolean {
  if (postalCode.length !== pattern.length) return false;

  for (let i = 0; i < pattern.length; i++) {
    const expected = pattern[i];
    const actual = postalCode[i];

    switch (expected) {
      case "N":
        if (!/\d/.test(actual)) return false;
        break;
      case "A":
        if (!/[A-Za-z]/.test(actual)) return false;
        break;
      case "X":
        if (!/[A-Za-z0-9]/.test(actual)) return false;
        break;
      default:
        if (actual !== expected) return false;
    }
  }

  return true;
}

const postalCodePatterns: Record<string, string[]> = {
  AU: ["NNNN"],
  AT: ["NNNN"],
  BE: ["NNNN"],
  BR: ["NNNNNNNN"],
  BG: ["NNNN"],
  CA: ["ANA NAN"],
  CL: ["NNNNNNN"],
  CN: ["NNNNNN"],
  CO: ["NNNNNN"],
  CY: ["NNNN"],
  CZ: ["NNNNN", "NNN NN"],
  DK: ["NNNN"],
  EE: ["NNNNN"],
  EC: ["NNNNNN"],
  FI: ["NNNNN"],
  FR: ["NNNNN"],
  DE: ["NNNNN"],
  GR: ["NNNNN", "NNN NN"],
  HU: ["NNNN"],
  IN: ["NNNNNN"],
  ID: ["NNNNN"],
  IE: ["ANN", "ANNXXXX", "ANN XXXX", "ANA", "ANAXXXX", "ANA XXXX"],
  IT: ["NNNNN"],
  JP: ["NNNNNNN"],
  KR: ["NNNNN"],
  LV: ["NNNN"],
  LT: ["NNNNN"],
  LU: ["NNNN"],
  MY: ["NNNNN"],
  MX: ["NNNNN"],
  NL: ["NNNN", "NNNNAA"],
  NO: ["NNNN"],
  PH: ["NNNN"],
  PL: ["NN-NNN"],
  PT: ["NNNN"],
  US: ["NNNNN"],
  RO: ["NNNNNN"],
  RU: ["NNNNNN", "NNN-NNN"],
  SA: ["NNNNN"],
  SG: ["NNNNNN"],
  SK: ["NNNNN", "NNN NN"],
  SI: ["NNNN"],
  ZA: ["NNNN"],
  LI: ["NNNN"],
  ES: ["NNNNN"],
  SE: ["NNNNN"],
  CH: ["NNNN"],
  TH: ["NNNNN"],
  TR: ["NNNNN"],
  UA: ["NNNNN"],
  GB: ["ANNAA", "ANNNAA", "ANANAA", "AANNAA", "AANA NAA", "AANN NAA"],
};
